// client/src/pages/ProfilePage.js
import React, { useState, useContext } from 'react';
import {
  Container, Grid, Paper, List, ListItemButton, ListItemText, ListSubheader, Typography, Box, Divider, Chip,
  Button, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import MyOrders from './MyOrders';
import AccountSettings from './AccountSettings';
import BecomeSeller from './BecomeSeller';
import MyProducts from './MyProducts';
import WishlistPage from './WishlistPage';
import MySellerAuctions from './MySellerAuctions';
import SellerInventory from './SellerInventory';
import SellerOrders from './SellerOrders';
import DeliveryPartners from './DeliveryPartners';
import AddressesPage from './AddressesPage';
import WatchlistPage from './WatchlistPage';
import MessagesPage from './MessagesPage';
import SellerAnalytics from './SellerAnalytics';
import SellerStorefront from './SellerStorefront';

// Also used to show the current section's name on the collapsed mobile nav bar.
// Admin sections live at real routes now (/admin/*, see App.js + AdminLayout)
// rather than as tabs here - a store's own admin panel is a whole separate
// area, not one more item in a customer's account sidebar.
const VIEW_LABELS = {
  dashboard: 'Dashboard', orders: 'My Orders', addresses: 'Addresses', wishlist: 'My Wishlist',
  watchlist: 'Watchlist', messages: 'Messages', settings: 'Account Settings',
  'my-products': 'My Products', 'seller-inventory': 'Inventory', 'my-auctions': 'My Auctions',
  'seller-orders': 'Orders to Fulfill', 'seller-analytics': 'Analytics', 'seller-storefront': 'Storefront',
  'delivery-partners': 'Delivery Partners', 'become-seller': 'Become a Seller',
};

const ProfilePage = () => {
  const { user, userProfile } = useContext(AuthContext);
  const { isEnabled } = useContext(FeatureFlagContext);
  const [activeView, setActiveView] = useState('dashboard');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // On mobile the nav list is long enough that it's worth collapsing out of the
  // way once a section is picked, rather than making every page start with a
  // scroll past ~20 links. Starts open so first-time visitors can see it.
  const [navOpen, setNavOpen] = useState(true);

  const goTo = (view) => {
    setActiveView(view);
    if (isMobile) setNavOpen(false);
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard': return <ProfileDashboard user={user} userProfile={userProfile} onNavigate={goTo} />;
      case 'orders': return <MyOrders />;
      case 'settings': return <AccountSettings />;
      case 'addresses': return <AddressesPage />;
      case 'wishlist': return <WishlistPage />;
      case 'watchlist': return <WatchlistPage />;
      case 'messages': return <MessagesPage />;
      case 'my-products': return <MyProducts />;
      case 'my-auctions': return <MySellerAuctions onNavigate={goTo} />;
      case 'become-seller': return <BecomeSeller />;
      case 'seller-inventory': return <SellerInventory />;
      case 'seller-orders': return <SellerOrders />;
      case 'seller-analytics': return <SellerAnalytics />;
      case 'seller-storefront': return <SellerStorefront />;
      case 'delivery-partners': return <DeliveryPartners />;
      default: return <ProfileDashboard user={user} userProfile={userProfile} onNavigate={goTo} />;
    }
  };

  const NavItem = ({ view, label }) => (
    <ListItemButton selected={activeView === view} onClick={() => goTo(view)} sx={{ borderRadius: 1, mb: 0.5 }}>
      <ListItemText primary={label} />
    </ListItemButton>
  );

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={3} lg={2}>
          {isMobile && !navOpen ? (
            <Button
              fullWidth variant="outlined" onClick={() => setNavOpen(true)}
              endIcon={<ExpandMoreIcon />}
              sx={{ justifyContent: 'space-between', px: 2, py: 1.25 }}
            >
              {VIEW_LABELS[activeView] || 'My Account'}
            </Button>
          ) : (
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" fontWeight={700}>My Account</Typography>
                {isMobile && (
                  <Button size="small" onClick={() => setNavOpen(false)} endIcon={<ExpandMoreIcon sx={{ transform: 'rotate(180deg)' }} />}>
                    Close
                  </Button>
                )}
              </Box>
              <List
                subheader={<ListSubheader disableSticky>General</ListSubheader>}
                dense
              >
                <NavItem view="dashboard" label="Dashboard" />
                <NavItem view="orders" label="My Orders" />
                <NavItem view="addresses" label="Addresses" />
                {isEnabled('wishlist') && <NavItem view="wishlist" label="My Wishlist" />}
                {isEnabled('auctions') && <NavItem view="watchlist" label="Watchlist" />}
                {isEnabled('live_chat') && <NavItem view="messages" label="Messages" />}
                <NavItem view="settings" label="Account Settings" />
              </List>

              {user && (user.role === 'seller' || user.role === 'admin') && (
                <List subheader={<ListSubheader disableSticky>Selling</ListSubheader>} dense>
                  <NavItem view="my-products" label="My Products" />
                  <NavItem view="seller-inventory" label="Inventory" />
                  {isEnabled('auctions') && <NavItem view="my-auctions" label="My Auctions" />}
                  <NavItem view="seller-orders" label="Orders to Fulfill" />
                  <NavItem view="seller-analytics" label="Analytics" />
                  <NavItem view="seller-storefront" label="Storefront" />
                  <NavItem view="delivery-partners" label="Delivery Partners" />
                </List>
              )}

              {isEnabled('become_seller') && user && user.role === 'customer' && (
                <List dense>
                  <NavItem view="become-seller" label="Become a Seller" />
                </List>
              )}

              {user && (user.role === 'admin' || user.role === 'super_admin') && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <List dense>
                    <ListItemButton component={Link} to="/admin" sx={{ borderRadius: 1, mb: 0.5, color: 'error.main' }}>
                      <ListItemText primary="Admin Panel" primaryTypographyProps={{ fontWeight: 700 }} />
                    </ListItemButton>
                  </List>
                </>
              )}
            </Paper>
          )}
        </Grid>
        <Grid item xs={12} md={9} lg={10}>
          <Paper sx={{ p: 3, minHeight: 400 }}>{renderContent()}</Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

const ProfileDashboard = ({ user, userProfile, onNavigate }) => {
  if (!user) return <Typography>Loading dashboard...</Typography>;
  return (
    <Box>
      <Typography variant="h4" fontWeight={700} gutterBottom>Welcome, {user.email}!</Typography>
      <Typography sx={{ mb: 0.5 }}><strong>Email:</strong> {user.email}</Typography>
      <Typography sx={{ mb: 0.5, textTransform: 'capitalize' }}><strong>Role:</strong> {user.role}</Typography>
      {userProfile?.client_number && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
          <Typography component="span"><strong>Client Number:</strong></Typography>
          <Chip label={userProfile.client_number} size="small" variant="outlined" />
        </Box>
      )}
      {userProfile?.loyalty_points > 0 && (
        <Typography
          sx={{ mb: 2, cursor: 'pointer', color: 'primary.main', display: 'inline-block' }}
          onClick={() => onNavigate?.('orders')}
        >
          You have <strong>{userProfile.loyalty_points} loyalty points</strong> — redeem them at checkout on your next order.
        </Typography>
      )}
      <Typography color="text.secondary">
        This is your personal dashboard. You can view your orders and manage your account settings from the sidebar.
      </Typography>
    </Box>
  );
};

export default ProfilePage;
